import React, { useRef, useState } from "react";
import { api, getReadableError } from "@/lib/api";
import { toast } from "sonner";
import AuthenticatedImage from "@/components/AuthenticatedImage";
import DocumentFormDialog, { DOC_TYPE_LABELS } from "@/components/DocumentFormDialog";
import ImageViewer from "@/components/ImageViewer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CloudArrowUp,
  ArrowsClockwise,
  DotsThreeVertical,
  Pencil,
  Trash,
  Eye,
  Camera,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import CameraCapture from "@/components/CameraCapture";

export default function DocumentCard({ doc, onUpdated, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [viewer, setViewer] = useState(null);

  const handleSaved = (updated) => {
    onUpdated && onUpdated(updated);
    setEditing(false);
  };
  const handleUploaded = (updated) => {
    onUpdated && onUpdated(updated);
  };
  const handleDelete = async () => {
    try {
      await api.delete(`/documents/${doc.id}`);
      setConfirmDelete(false);
      onDeleted && onDeleted(doc.id);
      toast.success("Documento eliminado");
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const title = doc.doc_label?.trim() || DOC_TYPE_LABELS[doc.doc_type] || "Documento";

  return (
    <div
      className="bg-white border border-slate-200 hover:border-slate-900 transition-colors fade-up"
      data-testid={`document-card-${doc.id}`}
    >
      {/* compact header row */}
      <div className="flex items-center gap-3 px-3 py-2.5 border-b border-slate-100">
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow text-[10px] text-slate-500 truncate">
            {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
          </div>
          <div className="font-display font-bold text-sm leading-tight truncate">{title}</div>
          {doc.doc_number && (
            <div className="font-mono-tab text-[11px] text-slate-500 mt-0.5">
              № {doc.doc_number}
            </div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger
            className="p-1 hover:bg-slate-100 shrink-0"
            data-testid={`document-menu-${doc.id}`}
          >
            <DotsThreeVertical size={16} weight="bold" />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="rounded-none border-slate-900" align="end">
            <DropdownMenuItem
              onClick={() => setEditing(true)}
              className="rounded-none gap-2"
              data-testid={`edit-document-${doc.id}`}
            >
              <Pencil size={14} weight="bold" /> Editar
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setConfirmDelete(true)}
              className="rounded-none gap-2 text-red-600 focus:text-red-700 focus:bg-red-50"
              data-testid={`delete-document-${doc.id}`}
            >
              <Trash size={14} weight="bold" /> Eliminar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* thumbnails */}
      <div className="grid grid-cols-2 gap-px bg-slate-200">
        <Thumb
          docId={doc.id}
          side="front"
          path={doc.front_path}
          label="Frontal"
          onUploaded={handleUploaded}
          onView={(path) => setViewer({ path, label: `${title} · Frontal` })}
        />
        <Thumb
          docId={doc.id}
          side="back"
          path={doc.back_path}
          label="Trasera"
          onUploaded={handleUploaded}
          onView={(path) => setViewer({ path, label: `${title} · Trasera` })}
        />
      </div>

      {doc.notes && (
        <div className="px-3 py-2 text-[11px] text-slate-600 border-t border-slate-100 line-clamp-2">
          {doc.notes}
        </div>
      )}

      <DocumentFormDialog
        open={editing}
        onOpenChange={setEditing}
        doc={doc}
        clientId={doc.client_id}
        onSaved={handleSaved}
      />
      <ImageViewer
        open={Boolean(viewer)}
        onOpenChange={(v) => !v && setViewer(null)}
        path={viewer?.path}
        label={viewer?.label}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent
          className="rounded-none border-slate-900"
          data-testid="confirm-delete-doc-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Eliminar documento</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la referencia y dejarás de ver las fotos. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-doc-button"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Thumb({ docId, side, path, label, onUploaded, onView }) {
  const galleryRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona una imagen");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Máximo 15MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("side", side);
      fd.append("file", file);
      const { data } = await api.post(`/documents/${docId}/upload`, fd);
      onUploaded && onUploaded(data);
      toast.success(`${label} subida`);
    } catch (err) {
      toast.error(getReadableError(err) || "Falló la subida");
    } finally {
      setUploading(false);
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const openGallery = () => galleryRef.current?.click();
  const openCamera = () => setCameraOpen(true);

  return (
    <div
      className="drop-zone relative group cursor-pointer aspect-[16/9] flex items-center justify-center"
      data-has-image={Boolean(path)}
      onClick={() => path && onView(path)}
      data-testid={`drop-zone-${side}-${docId}`}
    >
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`file-input-${side}-${docId}`}
      />
      <CameraCapture
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        label={label}
        onCapture={handleFile}
      />
      {path ? (
        <>
          <AuthenticatedImage
            path={path}
            alt={label}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-end justify-between p-1.5 pointer-events-none">
            <span className="bg-white text-slate-900 px-1.5 py-0.5 text-[9px] font-bold tracking-wider uppercase pointer-events-auto">
              {label}
            </span>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView(path);
                }}
                className="bg-white text-slate-900 p-1 hover:bg-[#6B21A8] hover:text-white transition-colors"
                title="Ver"
                data-testid={`view-${side}-${docId}`}
              >
                <Eye size={11} weight="bold" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openCamera();
                }}
                className="bg-white text-slate-900 p-1 hover:bg-[#6B21A8] hover:text-white transition-colors"
                title="Tomar foto"
                data-testid={`camera-replace-${side}-${docId}`}
              >
                <Camera size={11} weight="bold" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openGallery();
                }}
                className="bg-white text-slate-900 p-1 hover:bg-[#6B21A8] hover:text-white transition-colors"
                title="Elegir de galería"
                data-testid={`replace-${side}-${docId}`}
              >
                <ArrowsClockwise size={11} weight="bold" />
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center px-2 w-full">
          <CloudArrowUp size={18} weight="bold" className="mx-auto text-slate-400" />
          <div className="text-[10px] font-bold uppercase tracking-wider mt-0.5 text-slate-600">
            {label}
          </div>
          <div className="flex items-center justify-center gap-1 mt-1.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openCamera();
              }}
              className="inline-flex items-center gap-1 px-1.5 py-1 bg-[#6B21A8] text-white text-[9px] font-bold uppercase tracking-wider hover:bg-[#581C87] transition-colors"
              title="Tomar foto"
              data-testid={`btn-camera-${side}-${docId}`}
            >
              <Camera size={10} weight="bold" /> Foto
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openGallery();
              }}
              className="inline-flex items-center gap-1 px-1.5 py-1 border border-slate-700 text-slate-800 text-[9px] font-bold uppercase tracking-wider hover:bg-slate-900 hover:text-white transition-colors"
              title="Elegir de galería"
              data-testid={`btn-gallery-${side}-${docId}`}
            >
              <ImageIcon size={10} weight="bold" /> Galería
            </button>
          </div>
        </div>
      )}
      {uploading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
          <div className="text-[10px] font-bold uppercase tracking-wider">Subiendo...</div>
        </div>
      )}
    </div>
  );
}
